/**
 * RECORD REFUND DIALOG
 * Note le remboursement d'un trop-perçu sur un solde BED (TICKET-BED-25,
 * migration 00295). Le remboursement est fait à la main hors CRM (process de
 * la compta) : la RPC record_payment_refund n'en garde que la trace (montant,
 * date, commentaire, auteur) et le plafonne au trop-perçu restant. Les
 * encaissements ne sont jamais modifiés.
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from '../ui';
import { usePaymentActions } from '../../hooks/usePaymentActions';
import type { PaymentCaseWithRelations } from '../../hooks/usePaymentCase';
import { parseReceivedAmount } from '../../lib/payment/received-amount';

interface RecordRefundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentCase: PaymentCaseWithRelations;
  prospectName: string;
  productName: string;
  // Trop-perçu encore à rembourser (encaissé − montant − déjà remboursé).
  toRefundCents: number;
  onRecorded: () => void | Promise<void>;
}

function formatEuros(cents: number, currency: string): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(cents / 100);
}

// Date au format d'un <input type="date"> (heure locale du poste).
function toIsoDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function todayIsoDate(): string {
  return toIsoDate(new Date());
}

export function RecordRefundDialog({
  open,
  onOpenChange,
  paymentCase,
  prospectName,
  productName,
  toRefundCents,
  onRecorded,
}: RecordRefundDialogProps) {
  const [amountInput, setAmountInput] = useState('');
  const [refundedOn, setRefundedOn] = useState(todayIsoDate);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { recordRefund } = usePaymentActions();

  const today = todayIsoDate();
  // Un remboursement ne précède pas le dossier (même garde côté serveur).
  const minDate = toIsoDate(new Date(paymentCase.created_at));
  const toRefundFormatted = formatEuros(toRefundCents, paymentCase.currency);
  const amountCents = parseReceivedAmount(amountInput);
  const amountValid = amountCents !== null && amountCents > 0 && amountCents <= toRefundCents;
  // Comparaison de chaînes YYYY-MM-DD : ordre lexical = ordre chronologique.
  const dateValid = refundedOn !== '' && refundedOn >= minDate && refundedOn <= today;

  const canSubmit = amountValid && dateValid && !!comment.trim() && !submitting;

  function reset() {
    setAmountInput('');
    setRefundedOn(todayIsoDate());
    setComment('');
  }

  async function handleRecord() {
    if (!canSubmit || amountCents === null) return;

    setSubmitting(true);
    const result = await recordRefund({
      paymentCaseId: paymentCase.id,
      amountCents,
      refundedOn,
      comment: comment.trim(),
    });

    if (result.success) {
      const remaining = result.remainingToRefundCents ?? 0;
      toast.success(
        remaining > 0
          ? `Remboursement de ${formatEuros(amountCents, paymentCase.currency)} noté — reste ${formatEuros(remaining, paymentCase.currency)} à rembourser.`
          : `Remboursement de ${formatEuros(amountCents, paymentCase.currency)} noté — trop-perçu entièrement remboursé.`
      );
      reset();
      onOpenChange(false);
      await onRecorded();
    } else {
      toast.error(result.error ?? "Erreur lors de l'enregistrement du remboursement");
    }
    setSubmitting(false);
  }

  function handleClose(value: boolean) {
    if (!submitting) {
      reset();
      onOpenChange(value);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Noter un remboursement</DialogTitle>
          <DialogDescription>
            Prospect <strong>{prospectName}</strong> — Solde {productName} — trop-perçu restant à
            rembourser : <strong>{toRefundFormatted}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
            Le remboursement se fait à la main (virement au participant). Ce formulaire en garde
            seulement la trace sur la fiche ; aucun argent ne part du CRM.
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700" htmlFor="refund-amount">
              Montant remboursé (€) <span className="text-red-500">*</span>
            </label>
            <Input
              id="refund-amount"
              type="text"
              inputMode="decimal"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              placeholder={`Reste à rembourser : ${toRefundFormatted}`}
              disabled={submitting}
            />
            {amountInput.trim() !== '' && amountCents === null ? (
              <p className="text-xs text-red-500">
                Montant illisible : chiffres avec au plus 2 décimales (ex. 100,50).
              </p>
            ) : amountInput.trim() !== '' && !amountValid ? (
              <p className="text-xs text-red-500">
                Saisir un montant supérieur à 0 et au plus égal au reste à rembourser (
                {toRefundFormatted}).
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700" htmlFor="refund-date">
              Date du remboursement <span className="text-red-500">*</span>
            </label>
            <Input
              id="refund-date"
              type="date"
              value={refundedOn}
              min={minDate}
              max={today}
              onChange={(e) => setRefundedOn(e.target.value)}
              disabled={submitting}
            />
            {!dateValid && (
              <p className="text-xs text-red-500">
                Date obligatoire, entre la création du dossier et aujourd&apos;hui.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700" htmlFor="refund-comment">
              Commentaire <span className="text-red-500">*</span>
            </label>
            <Textarea
              id="refund-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Ex: Virement de remboursement du 12/11, réf. bancaire VIR-..."
              rows={3}
              disabled={submitting}
            />
            <p className="text-xs text-zinc-400">
              Ce commentaire sera enregistré dans la timeline du dossier.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleRecord}
            disabled={!canSubmit}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {submitting ? 'Enregistrement...' : 'Noter le remboursement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
